import * as React from "react";

import { useAuth } from "@/providers";
import type { TableUIPreferences } from "@/shared/domain/config/ConfigRecords";
import {
  useSearchTablePreferences,
  useUpdateSearchTablePreferences,
} from "@/shared/domain/config/hooks";
import { searchTablePreferenceKeyFor } from "@/shared/domain/config/searchTablePreferenceKeys";
import { useCheckFeatureFlag } from "@/shared/domain/featureFlags/hooks";
import type { QueryTarget } from "@/shared/domain/query";
import type { ViewRecord } from "@/shared/domain/views";
import type {
  DataTablePreferences,
  DataTableSort,
} from "@/shared/foundation/primitives/Table";

import {
  effectiveLayout,
  layoutAfter,
  overlayDraft,
  sameLayout,
  type TableLayout,
} from "./layoutDrafts";
import {
  layoutKey,
  parseLayout,
  readLayout,
  removeLayout,
  subscribeToLayouts,
  writeLayout,
} from "./viewLayoutStorage";

export interface UseLayoutDraftsOptions {
  /** Resource type this list shows; drafts are kept per table. */
  target: QueryTarget;
  /** The list's built-in layout, which every draft is measured against. */
  baseline: TableLayout;
}

/**
 * The `useTable` options a list hands over so the table starts on the bare
 * layout and reports every change here. Spread into the list's own options.
 */
export interface LayoutTableOptions {
  /** The bare layout's sort at mount, or `undefined` for an unsorted table. */
  initialSort: DataTableSort | undefined;
  /** The bare layout at mount: columns, rows per page and sort, every one stated. */
  preferences: DataTablePreferences;
  /**
   * Receives every layout change the table makes. Forwards to the current
   * `recordLayout`, so a table that outlives an org switch still records
   * against the org on screen.
   */
  onPreferencesChange: (next: DataTablePreferences) => void;
  /** The built-in column order, for a table that mounts with no stored columns. */
  defaultVisibleColumnIds: string[];
}

export interface LayoutDrafts {
  /** The built-in layout this hook was given. */
  baseline: TableLayout;
  /**
   * What the bare table opens on: the built-in layout, or this person's
   * draft of the bare table when they have one. Every dimension stated, so it
   * can seed the table at mount directly.
   */
  bareLayout: TableLayout;
  /** `bareLayout` as table options, captured once at mount, with `recordLayout` as the change handler. */
  tableOptions: LayoutTableOptions;
  /** This person's draft on a View, or on the bare table with `null`; `null` when there is none. */
  draftFor: (viewId: string | null) => TableLayout | null;
  /**
   * Judge a layout the table emitted against the View it is on: kept as that
   * View's draft when it differs from what the View shows, dropped when it no
   * longer does. Idempotent, so the grid reporting the same layout twice
   * costs nothing.
   */
  recordLayout: (next: DataTablePreferences) => void;
  /**
   * Tell the hook which View the table is on, or is about to be moved onto,
   * and the sort its query carries if it is a RoboQL View. The apply path
   * calls this *before* it moves the table, so every change the move produces
   * is judged against the View it belongs to. Also rechecks that View's draft
   * against what the View now shows and drops it if they agree — which is how
   * saving a draft into the View retires the draft.
   */
  setLoaded: (
    view: ViewRecord | null,
    querySort?: DataTableSort | null,
  ) => void;
  /**
   * Take deleted custom fields out of the bare table's columns, leaving the
   * rest of its draft as it is. For the custom-field pruning that corrects a
   * remembered order when a field is deleted. `columns` is the pruned order
   * the table opened on, which can be a View's, so it is used only before
   * Views, when the bare table's memory is the only order there is.
   */
  pruneColumns: (next: { columns: string[]; removedColumns: string[] }) => void;
}

/**
 * Remember what each person has changed on top of each View, and on the bare
 * table, until they save it or reset it.
 *
 * A View is the only layout stored on the server. A draft is what one person
 * has done on top of one since — columns dragged, a sort chosen, a page size
 * picked — and it lives in this browser's `localStorage`, keyed by user, org,
 * table and View, the way every other draft in the app does. A draft exists
 * exactly while the live layout differs from what its View shows (or from the
 * built-in layout, for the bare table). Saving the layout into the View, or
 * resetting to the View, leaves the two equal and the draft is dropped;
 * nothing has to remember to clear it. Nobody else sees a draft, and the way
 * to carry a layout to another machine is to save it as a View.
 *
 * Every change the table reports is judged at once, against the View named
 * by the last `setLoaded`. Applying a View moves the table in several steps,
 * so the apply path names the View first and moves the table second; the
 * steps are then judged against the View they produce, and since judging the
 * same layout twice reaches the same answer, the grid echoing a step back is
 * harmless.
 *
 * Before Views (`release-views` off for the org), the table-preferences record
 * is the bare table's memory, as it always was: its columns and page size are
 * read at mount and written on every change. That path exists so an org
 * without Views loses nothing, and it goes away with the flag in ENG-2960,
 * which migrates each record into a default View. With the flag on the record
 * is neither read for layout nor written, so it stays intact for that
 * migration.
 */
export function useLayoutDrafts({
  target,
  baseline,
}: UseLayoutDraftsOptions): LayoutDrafts {
  const { currentOrganization, currentUser } = useAuth();
  const orgId = currentOrganization?.org_id ?? null;
  const userId = currentUser?.user_id ?? null;
  // Resolved before the list mounts: every route that renders one of these
  // lists awaits the flag states in its loader.
  const viewsEnabled = useCheckFeatureFlag("release-views");
  const configKey = searchTablePreferenceKeyFor(target);
  const preferences = useSearchTablePreferences(configKey);
  const { mutate } = useUpdateSearchTablePreferences(configKey);

  const stored: TableUIPreferences | null =
    orgId === null
      ? null
      : (preferences.data?.org_scoped_preferences[orgId] ?? null);

  const keyFor = React.useCallback(
    (viewId: string | null): string | null =>
      userId === null || orgId === null || !viewsEnabled
        ? null
        : layoutKey(userId, orgId, target, viewId),
    [orgId, target, userId, viewsEnabled],
  );

  const draftFor = React.useCallback(
    (viewId: string | null): TableLayout | null => {
      const key = keyFor(viewId);
      return key === null ? null : readLayout(localStorage, key);
    },
    [keyFor],
  );

  // The bare entry is read through the store's subscription so a write made
  // here, or in another tab, shows in `bareLayout` without storage being
  // reactive on its own. The raw string is the snapshot: it is a stable value
  // between writes, which is what `useSyncExternalStore` needs.
  const bareKey = keyFor(null);
  const bareRaw = React.useSyncExternalStore(subscribeToLayouts, () =>
    bareKey === null ? null : localStorage.getItem(bareKey),
  );

  const bareLayout = React.useMemo<TableLayout>(() => {
    if (viewsEnabled) {
      return overlayDraft(baseline, parseLayout(bareRaw));
    }
    return {
      visibleColumns: stored?.columns ?? baseline.visibleColumns,
      sort: baseline.sort,
      pageSize: stored?.rows_per_page ?? baseline.pageSize,
    };
  }, [bareRaw, baseline, stored, viewsEnabled]);

  const loadedRef = React.useRef<{
    view: ViewRecord | null;
    querySort: DataTableSort | null;
  }>({ view: null, querySort: null });

  const setLoaded = React.useCallback(
    (view: ViewRecord | null, querySort: DataTableSort | null = null) => {
      loadedRef.current = { view, querySort };
      if (view === null) {
        return;
      }
      // Saving a draft into its View changes the record and leaves the table
      // where it was, so nothing is emitted and the draft would outlive the
      // state it described. Judged here instead, against the View as it now is.
      const key = keyFor(view.view_id);
      if (key === null) {
        return;
      }
      const draft = readLayout(localStorage, key);
      if (
        draft !== null &&
        sameLayout(
          draft,
          effectiveLayout(view.definition.display, baseline, querySort),
        )
      ) {
        removeLayout(localStorage, key);
      }
    },
    [baseline, keyFor],
  );

  const recordLayout = React.useCallback(
    (latest: DataTablePreferences) => {
      if (orgId === null) {
        return;
      }
      const { view, querySort } = loadedRef.current;
      const shown = effectiveLayout(
        view?.definition.display ?? null,
        baseline,
        querySort,
      );
      const whole = layoutAfter(shown, latest);

      if (!viewsEnabled) {
        mutate({
          orgId,
          preferences: {
            columns: whole.visibleColumns,
            rows_per_page: whole.pageSize,
          },
        });
        return;
      }

      const key = keyFor(view?.view_id ?? null);
      if (key === null) {
        return;
      }
      if (sameLayout(whole, shown)) {
        removeLayout(localStorage, key);
      } else {
        writeLayout(localStorage, key, whole);
      }
    },
    [baseline, keyFor, mutate, orgId, viewsEnabled],
  );

  // The table hands its changes to whatever `onPreferencesChange` it was given
  // at mount, so that callback must stay valid for the table's whole life —
  // across an org switch included, when `recordLayout` itself is rebuilt.
  const recordLayoutRef = React.useRef(recordLayout);
  recordLayoutRef.current = recordLayout;

  const pruneColumns = React.useCallback(
    ({
      columns,
      removedColumns,
    }: {
      columns: string[];
      removedColumns: string[];
    }) => {
      if (orgId === null) {
        return;
      }
      if (!viewsEnabled) {
        mutate({ orgId, preferences: { columns } });
        return;
      }
      const key = keyFor(null);
      if (key === null) {
        return;
      }
      // With no draft the bare table shows the built-in columns, which hold
      // no custom fields to remove.
      const draft = readLayout(localStorage, key);
      if (draft === null) {
        return;
      }
      const removed = new Set(removedColumns);
      const pruned: TableLayout = {
        ...draft,
        visibleColumns: draft.visibleColumns.filter((id) => !removed.has(id)),
      };
      if (sameLayout(pruned, baseline)) {
        removeLayout(localStorage, key);
      } else {
        writeLayout(localStorage, key, pruned);
      }
    },
    [baseline, keyFor, mutate, orgId, viewsEnabled],
  );

  // Captured once: `useTable` reads its options at mount and never again, and
  // a `preferences` object that changed with every write would only mislead
  // a reader into thinking the table follows it.
  const [tableOptions] = React.useState<LayoutTableOptions>(() => ({
    initialSort: bareLayout.sort ?? undefined,
    preferences: {
      columns: bareLayout.visibleColumns,
      rowsPerPage: bareLayout.pageSize,
      sort: bareLayout.sort,
    },
    onPreferencesChange: (next) => recordLayoutRef.current(next),
    defaultVisibleColumnIds: baseline.visibleColumns,
  }));

  return React.useMemo(
    () => ({
      baseline,
      bareLayout,
      tableOptions,
      draftFor,
      recordLayout,
      setLoaded,
      pruneColumns,
    }),
    [
      baseline,
      bareLayout,
      tableOptions,
      draftFor,
      recordLayout,
      setLoaded,
      pruneColumns,
    ],
  );
}
